import { BrowserRouter, Routes, Route, Navigate, useSearchParams } from "react-router-dom";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { Toaster } from "sonner";
import { HelmetProvider } from "react-helmet-async";
import { AuthProvider } from "./contexts/auth-context";
import { ProtectedRoute } from "./components/protected-route";
import { MainLayout } from "./components/layout/main-layout";
import { LoginPage } from "./pages/login";
import { ApplicationsListPage } from "./pages/applications/list";
import { ApplicationDetailPage } from "./pages/applications/detail";
import { AdminLendersPage } from "./pages/admin/lenders";
import { AdminStipsPage } from "./pages/admin/stips";
import { AdminReportsPage } from "./pages/admin/reports";
import { AdminMarketplaceSourceDealersPage } from "./pages/admin/marketplace-source-dealers";
import { NotificationPreferencesPage } from "./pages/notification-preferences";
import { OrgSettingsPage } from "./pages/settings/org-settings";
import { AmSettingsPage } from "./pages/settings/am-settings";
import { StaffCreateOrgPage } from "./pages/staff/create-org";
import { TsmCreateDealerPage } from "./pages/tsm/create-dealer";
import { PublicIntakePage } from "./pages/public/apply";
import { OrgLinkRedirectPage } from "./pages/public/org-link-redirect";
import { ThankYouPage } from "./pages/public/thank-you";
import { CustomerSignPage } from "./pages/public/customer-sign";
import { ErrorPage } from "./pages/public/error";
import { PublicUploadPage } from "./pages/public/upload";
import { LandingPage } from "./pages/landing";
import { ArticlesIndexPage } from "./pages/public/marketing/articles-index";
import { ArticleDetailPage } from "./pages/public/marketing/article-detail";
import { TradeshowsIndexPage } from "./pages/public/marketing/tradeshows-index";
import { TradeshowDetailPage } from "./pages/public/marketing/tradeshow-detail";
import { UpdatesIndexPage } from "./pages/public/marketing/updates-index";
import { UpdateDetailPage } from "./pages/public/marketing/update-detail";
import { SetPasswordPage } from "./pages/set-password";
import { ForgotPasswordPage } from "./pages/forgot-password";
import { ResetPasswordPage } from "./pages/reset-password";
import { AccountPasswordPage } from "./pages/account-password";
import { PrivacyPage } from "./pages/public/privacy";
import { TermsPage } from "./pages/public/terms";
import { NotFoundPage } from "./pages/public/not-found";
import { DemoPage } from "./pages/public/demo";
import { ScrollToTop } from "./components/scroll-to-top";

/** Redirects /verify-email?token=... to /set-password?token=... (dealer signup and invite use same token flow). */
function VerifyEmailRedirect() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token") ?? "";
  if (!token) return <Navigate to="/login" replace />;
  return <Navigate to={`/set-password?token=${encodeURIComponent(token)}`} replace />;
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
});

function App() {
  return (
    <HelmetProvider>
      <QueryClientProvider client={queryClient}>
        <AuthProvider>
          <BrowserRouter>
            <ScrollToTop />
            <Routes>
              <Route path="/login" element={<LoginPage />} />
              <Route path="/forgot-password" element={<ForgotPasswordPage />} />
              <Route path="/reset-password" element={<ResetPasswordPage />} />
            <Route
              path="/apps"
              element={
                <ProtectedRoute>
                  <MainLayout>
                    <ApplicationsListPage />
                  </MainLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/apps/:id"
              element={
                <ProtectedRoute>
                  <MainLayout>
                    <ApplicationDetailPage />
                  </MainLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/lenders"
              element={
                <ProtectedRoute requiredRole="SUPERADMIN">
                  <MainLayout>
                    <AdminLendersPage />
                  </MainLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/stips"
              element={
                <ProtectedRoute
                  requiredRole={["ADMIN", "FI_MANAGER", "SUPERADMIN", "DD_ACCOUNT_MANAGER"]}
                >
                  <MainLayout>
                    <AdminStipsPage />
                  </MainLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/reports"
              element={
                <ProtectedRoute requiredRole={["SUPERADMIN", "DD_ACCOUNT_MANAGER", "ORG_ADMIN", "TERRITORY_SALES_MANAGER"]}>
                  <MainLayout>
                    <AdminReportsPage />
                  </MainLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/marketplace-source-dealers"
              element={
                <ProtectedRoute requiredRole="SUPERADMIN">
                  <MainLayout>
                    <AdminMarketplaceSourceDealersPage />
                  </MainLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/notification-preferences"
              element={
                <ProtectedRoute>
                  <MainLayout>
                    <NotificationPreferencesPage />
                  </MainLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/settings/am"
              element={
                <ProtectedRoute requiredRole="DD_ACCOUNT_MANAGER">
                  <MainLayout>
                    <AmSettingsPage />
                  </MainLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/settings/org"
              element={
                <ProtectedRoute requiredRole={["SUPERADMIN", "DD_ACCOUNT_MANAGER", "ORG_ADMIN"]}>
                  <MainLayout>
                    <OrgSettingsPage />
                  </MainLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/staff/create-org"
              element={
                <ProtectedRoute requiredRole={["SUPERADMIN", "DD_ACCOUNT_MANAGER"]}>
                  <MainLayout>
                    <StaffCreateOrgPage />
                  </MainLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/tsm/create-dealer"
              element={
                <ProtectedRoute requiredRole="TERRITORY_SALES_MANAGER">
                  <MainLayout>
                    <TsmCreateDealerPage />
                  </MainLayout>
                </ProtectedRoute>
              }
            />
            <Route path="/apply/org/:token" element={<OrgLinkRedirectPage />} />
            <Route path="/apply/:token" element={<PublicIntakePage />} />
            <Route path="/apply/rep/:token" element={<PublicIntakePage isSalesRepLink={true} />} />
            <Route path="/public/uploads/:token" element={<PublicUploadPage />} />
            <Route path="/customer-sign/:token" element={<CustomerSignPage />} />
              <Route path="/thank-you" element={<ThankYouPage />} />
              <Route path="/error" element={<ErrorPage />} />

              <Route path="/articles" element={<ArticlesIndexPage />} />
              <Route path="/articles/:slug" element={<ArticleDetailPage />} />
              <Route path="/tradeshows" element={<TradeshowsIndexPage />} />
              <Route path="/tradeshows/:slug" element={<TradeshowDetailPage />} />
              <Route path="/updates" element={<UpdatesIndexPage />} />
              <Route path="/updates/:slug" element={<UpdateDetailPage />} />

              <Route path="/verify-email" element={<VerifyEmailRedirect />} />
              <Route path="/set-password" element={<SetPasswordPage />} />
            <Route
              path="/account/password"
              element={
                <ProtectedRoute>
                  <MainLayout>
                    <AccountPasswordPage />
                  </MainLayout>
                </ProtectedRoute>
              }
            />
              <Route path="/privacy" element={<PrivacyPage />} />
              <Route path="/terms" element={<TermsPage />} />
              <Route path="/demo" element={<DemoPage />} />
              <Route path="/finance-account" element={<Navigate to="/login" replace />} />
              <Route path="/" element={<LandingPage />} />
              <Route path="*" element={<NotFoundPage />} />
            </Routes>
          </BrowserRouter>
          <Toaster position="top-right" richColors closeButton />
        </AuthProvider>
      </QueryClientProvider>
    </HelmetProvider>
  );
}

export default App;
